import React from 'react';
import { FiSend, FiTrash2 } from 'react-icons/fi';
import type { ChannelDashboardOverview, ChannelMediaResponse } from '@troia-v3/types';

import { useDeleteChannelMedia, usePublishChannelMedia } from '../../hooks';
import { getProviderLabel } from '../../utils/getProviderLabel';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

const badgeClass = (status: string): string =>
  status === 'scheduled' || status === 'publishing' ? 'scheduled' : status === 'failed' ? 'failed' : 'draft';
const badgeLabel = (status: string): string =>
  status === 'scheduled' ? 'Agendado' : status === 'failed' ? 'Falhou' : status === 'publishing' ? 'Publicando…' : 'Rascunho';

const formatWhen = (iso?: string): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

export const ScheduledQueue: React.FC<{ channelId: string; channel: ChannelDashboardOverview['channel']; items: ChannelMediaResponse[] }> = ({
  channelId,
  channel,
  items,
}) => {
  const confirm = useConfirm();
  const providerLabel = getProviderLabel(channel.providerId);
  const publishMutation = usePublishChannelMedia(channelId, providerLabel);
  const deleteMutation = useDeleteChannelMedia(channelId);

  if (items.length === 0) {
    return (
      <div className="cdash-empty" data-testid="cdash-queue-empty">
        <div className="cdash-empty-t">Nada na fila</div>
        <div className="cdash-empty-d">Rascunhos e agendamentos aparecem aqui.</div>
      </div>
    );
  }

  return (
    <div className="cdash-sync-grid" data-testid="cdash-queue">
      {items.map((m) => {
        const first = m.assets[0];
        const isPublishing = m.status === 'publishing';
        return (
          <div className="cdash-queue-item" key={m.id} data-testid={`cdash-queue-${m.id}`}>
            <div className="cdash-queue-thumb">
              {first ? (first.type === 'video' ? <video src={first.mediaUrl} muted /> : <img src={first.mediaUrl} alt="" />) : null}
            </div>
            <div className="cdash-queue-body">
              <div className="cdash-queue-cap">{m.caption || 'Sem legenda'}</div>
              <div className="cdash-queue-meta">
                <span className={`cdash-queue-badge ${badgeClass(m.status)}`}>{badgeLabel(m.status)}</span>
                {m.status === 'scheduled' && m.scheduledAt ? <span>{formatWhen(m.scheduledAt)}</span> : null}
                {m.status === 'failed' && m.failReason ? <span>{m.failReason}</span> : null}
              </div>
            </div>
            <div className="cdash-queue-actions">
              <button
                className="cdash-iconbtn"
                title="Publicar agora"
                onClick={async () => {
                  if (await confirm({ message: `Publicar agora no ${providerLabel}? Esta ação não pode ser desfeita.`, confirmButtonText: 'Publicar agora', variant: 'default' })) publishMutation.mutate(m.id);
                }}
                disabled={publishMutation.isPending || isPublishing}
                data-testid={`cdash-queue-publish-${m.id}`}
              >
                <FiSend size={15} />
              </button>
              <button
                className="cdash-iconbtn danger"
                title="Excluir"
                onClick={() => deleteMutation.mutate(m.id)}
                disabled={deleteMutation.isPending || isPublishing}
                data-testid={`cdash-queue-delete-${m.id}`}
              >
                <FiTrash2 size={15} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
